import * as E                from '../errors';
import { Event, BoundEvent } from './Event';
import { Target            } from './Target';
export type TListenerCallback<
	TTarget extends TBaseTarget,
	TBaseTarget extends Target<TBaseTarget, TBaseTarget>
> = ((event : BoundEvent<TTarget, TBaseTarget>) => (any | Promise<any>));
export interface IListenerDescriptor<
	TTarget extends TBaseTarget,
	TBaseTarget extends Target<TBaseTarget, TBaseTarget>,
	TImplementationInfos extends Record<string, any> | undefined = any
> {
	isCapture?               : boolean;
	isAtTarget?              : boolean;
	isOnce?                  : boolean;
	type                     : string;
	subType?                 : string | null;
	callback                 : TListenerCallback<TTarget, TBaseTarget>;
	onlyForTargetsId?        : unknown[];
	listener                 : TBaseTarget;
	target                   : TTarget;
	implementationInfos?     : TImplementationInfos;
	disableEventExistsCheck? : boolean;
	/**
	 * hight priority  = < 0
	 * normal priority = 0
	 * low priority = > 0
	 * if no priority was given, normal priority assumed
	 **/
	priority? : number;
};
export class Listener<
	TTarget extends TBaseTarget,
	TBaseTarget extends Target<TBaseTarget, TBaseTarget>,
	TImplementationInfos extends Record<string, any> | undefined = any
> {
	private readonly callback         : TListenerCallback<TTarget, TBaseTarget>;
	readonly isCapture                : boolean;
	readonly isAtTarget               : boolean;
	readonly isOnce                   : boolean;
	readonly type                     : string;
	readonly subType                  : string | null;
	readonly disableEventExistsCheck? : boolean;
	onlyForTargetsId                  : unknown[];
	priority                          : number;
	listener                          : TBaseTarget;
	target                            : TTarget;
	readonly implementationInfos      : TImplementationInfos | null;
	async handleEvent(event : Event<TTarget, TBaseTarget>) {
		//Bridge made by prototype are faster and lighter but have rewrite / override problem so use proxy for the times.
		//Later re use better prototype implementation or use AOT proxy with garbage collector connection to recycle unused proxy.
		// const bridge = <BoundEvent<TTarget>>Object.assign(Object.create(event), {
		// 	listener    : this,
		// 	constructor : Event
		// });
		const listener = this;
		const bridge : BoundEvent<TTarget, TBaseTarget> = <any>new Proxy(event, {
			get(target, prop, receiver) {
				if (prop === 'listener') {
					return (listener);
				}
				return (Reflect.get(target, prop, receiver));
			}
		});
		await this.callback(bridge);
	};
	constructor(desc : IListenerDescriptor<TTarget, TBaseTarget, TImplementationInfos>) {
		if (desc.isCapture && desc.isAtTarget) {
			throw E.user(
				'Cannot use isCapture and isAtTarget in the same time!',
				{ descriptor : desc }
			);
		}
		this.isCapture               = desc.isCapture        || false;
		this.isAtTarget              = desc.isAtTarget       || false;
		this.isOnce                  = desc.isOnce           || false;
		this.type                    = desc.type;
		this.subType                 = desc.subType          || null;
		this.onlyForTargetsId        = desc.onlyForTargetsId || [];
		this.priority                = desc.priority         || 0;
		this.callback                = desc.callback;
		this.listener                = desc.listener;
		this.target                  = desc.target;
		this.implementationInfos     = desc.implementationInfos || null;
		this.disableEventExistsCheck = desc.disableEventExistsCheck;
	};
};
export type TListener<
	TTarget extends TBaseTarget,
	TBaseTarget extends Target<TBaseTarget, TBaseTarget>
> = Listener<TTarget, TBaseTarget> | TListenerCallback<TTarget, TBaseTarget>;
